import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Modal from '../Modal';
import { getSelectStyles } from '../../utility/selectStyles';
import {
    EditField, InfoColumn, TableInfo, buildInsert, buildUpdate, fieldKind, fieldLabel, fieldPlaceholder, filledByDatabase, initialField,
} from './rowForm';

/** What the view shows inside the modal about the write it is running, or the one that just failed. */
export interface WriteState {
    /** "Saving...", "Checking...": a write is running; every control is off and Escape with it. */
    busy: string | null;
    /** One short line for a failed save, the input kept. */
    line: string | null;
    /** row_changed: the line comes with Reload. */
    changed?: boolean;
    /** The outcome is not known: the form cannot be sent again (it may have been saved), only Reload. */
    locked?: boolean;
    /** Expert mode: Postgres's own text, under the line. */
    postgres?: string;
}

interface RowFormModalProps {
    mode: 'add' | 'edit';
    info: TableInfo;
    expert: boolean;
    write: WriteState;
    /** Edit: each column's old value, the whole cell once a cut one is loaded; a column missing here is read-only. */
    originals?: Record<string, string | null>;
    /** Edit: the cut cells still loading, and those that cannot be edited here, each with its line. */
    loading?: ReadonlySet<string>;
    locked?: ReadonlyMap<string, string>;
    onAdd?: (columns: string[], row: (string | { default: true })[]) => void;
    onSave?: (changes: { column: string; old: string | null; new: string | null }[]) => void;
    onDelete?: () => void;
    onReload?: () => void;
    onClose: () => void;
    /** E27: a single-column foreign-key field gets Pick, which asks the view for a row and fills the field with it. */
    pickable?: (column: string) => boolean;
    onPick?: (column: string, fill: (value: string) => void) => void;
}

const YES_NO = [{ value: 't', label: 'Yes' }, { value: 'f', label: 'No' }];

/**
 * Add row and Edit row (DESIGN_EDITING.md console flows 4 and 5). The form lives in this component's state only.
 * ⛔ Every value and column name is the customer's: React text, field values and labels only.
 */
const RowFormModal: React.FC<RowFormModalProps> = ({
    mode, info, expert, write, originals, loading, locked, onAdd, onSave, onDelete, onReload, onClose, pickable, onPick,
}) => {
    const editing = mode === 'edit';
    const [values, setValues] = useState<Record<string, string>>({});
    const [fields, setFields] = useState<Record<string, EditField>>({});
    const [formLine, setFormLine] = useState<{ column: string; line: string } | null>(null);
    const busy = write.busy !== null;

    // A cut cell's whole value arrives after the modal opens: its field starts from it then, once.
    useEffect(() => {
        if (!editing || !originals) return;
        setFields((f) => {
            const next = { ...f };
            for (const [k, v] of Object.entries(originals)) if (!(k in next)) next[k] = initialField(v);
            return next;
        });
    }, [editing, originals]);

    const readOnly = (c: InfoColumn): boolean => busy || filledByDatabase(c) || (editing && (
        c.in_key || !originals || !(c.name in originals) || !!loading?.has(c.name) || !!locked?.has(c.name)));

    const textOf = (c: InfoColumn): string => (editing ? fields[c.name]?.text ?? '' : values[c.name] ?? '');
    const setText = (c: InfoColumn, text: string) => {
        setFormLine(null);
        if (editing) setFields((f) => ({ ...f, [c.name]: { text, isNull: false } }));
        else setValues((v) => ({ ...v, [c.name]: text }));
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (busy || write.locked) return;
        if (editing) {
            const b = buildUpdate(info, originals ?? {}, fields, expert);
            if (!b.ok) { setFormLine({ column: b.column, line: b.line }); return; }
            if (b.changes.length === 0) { onClose(); return; }
            onSave?.(b.changes);
        } else {
            const b = buildInsert(info, values);
            if (!b.ok) { setFormLine({ column: b.column, line: b.line }); return; }
            onAdd?.(b.columns, b.row);
        }
    };

    const input = (c: InfoColumn, id: string) => {
        const kind = fieldKind(c.type);
        const value = textOf(c);
        const off = readOnly(c);
        const isNull = editing && fields[c.name]?.isNull === true;
        const placeholder = loading?.has(c.name) ? 'Loading...' : isNull ? 'NULL' : fieldPlaceholder(c);
        if (kind === 'yesno' && !off) {
            return (
                <Select
                    inputId={id}
                    options={YES_NO}
                    value={YES_NO.find((o) => o.value === value) ?? null}
                    onChange={(o: any) => setText(c, o?.value ?? '')}
                    isClearable
                    placeholder={placeholder}
                    styles={getSelectStyles()}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            );
        }
        if (kind === 'json' || kind === 'textarea') {
            return <textarea id={id} rows={kind === 'json' ? 4 : Math.min(6, Math.max(1, value.split('\n').length))} value={value} placeholder={placeholder} disabled={off} onChange={(e) => setText(c, e.target.value)} />;
        }
        // Edit shows dates and times as the page read them, so an untouched field is never a change
        const type = !editing && (kind === 'datetime' || kind === 'datetime_nozone') ? 'datetime-local'
            : kind === 'date' && (!editing || /^\d{4}-\d{2}-\d{2}$/.test(value) || value === '') ? 'date' : 'text';
        return (
            <input
                id={id}
                type={type}
                {...(type === 'datetime-local' ? { step: 1 } : {})}
                value={value}
                placeholder={placeholder}
                disabled={off}
                onChange={(e) => setText(c, e.target.value)}
            />
        );
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={editing ? 'Edit row' : 'Add row'}
            size="md"
            closeOnEscape={!busy}
            showCloseButton={!busy}
        >
            <form onSubmit={submit} className="db-data-form db-data-row-form">
                {info.columns.map((c, i) => {
                    const id = `db-data-field-${i}`;
                    const lockedLine = locked?.get(c.name);
                    const nullable = !c.not_null && !readOnly(c);
                    return (
                        <div className="form-group" key={i}>
                            <label htmlFor={id}>{fieldLabel(c)}</label>
                            {editing && !filledByDatabase(c) && !c.in_key && !(c.name in (originals ?? {})) && !loading?.has(c.name)
                                ? <input id={id} type="text" value="" disabled placeholder={fieldPlaceholder(c)} />
                                : onPick && pickable?.(c.name) && !readOnly(c) ? (
                                    <div className="db-data-field-with-pick">
                                        {input(c, id)}
                                        <button type="button" className="db-data-text-button" aria-label={`Pick ${c.name}`}
                                            onClick={() => onPick(c.name, (value) => setText(c, value))}>Pick</button>
                                    </div>
                                ) : input(c, id)}
                            {editing && expert && nullable && (
                                <label className="db-data-null-toggle">
                                    <input
                                        type="checkbox"
                                        checked={fields[c.name]?.isNull === true}
                                        onChange={(e) => {
                                            setFormLine(null);
                                            const on = e.target.checked;
                                            setFields((f) => ({ ...f, [c.name]: { text: on ? '' : f[c.name]?.text ?? '', isNull: on } }));
                                        }}
                                    />
                                    NULL
                                </label>
                            )}
                            {lockedLine && <p className="db-data-field-line">{lockedLine}</p>}
                            {formLine?.column === c.name && <p className="db-data-field-line" role="alert">{formLine.line}</p>}
                        </div>
                    );
                })}
                {write.line && (
                    <div className="db-data-write-line" role="alert">
                        <span>{write.line}</span>
                        {(write.changed || write.locked) && onReload && <button type="button" className="db-data-text-button" onClick={onReload}>Reload</button>}
                    </div>
                )}
                {expert && write.postgres && <pre className="db-data-write-postgres">{write.postgres}</pre>}
                <div className="modal-actions">
                    {editing && onDelete && (
                        <button type="button" className="db-data-delete-button" onClick={onDelete} disabled={busy || write.locked}>Delete</button>
                    )}
                    <button type="button" className="cancel-button" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="submit" className="submit-button" disabled={busy || write.locked || (editing && (loading?.size ?? 0) > 0)}>
                        {write.busy ?? (editing ? 'Save' : 'Add')}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default RowFormModal;
